import {
  IFulfilledChat,
  MessageOperation,
  TMessage
} from "../../../interfaces/interfacesChat";
import React, { useEffect, useRef } from "react";
import { MessageItem } from "./MessageItem";
import format from "date-fns/format";
import { Skeleton, Space } from "antd";
import { addScrollOnTopListener, scrollElementTo } from "../../../utils/dom";
import { ThreeDotsLoader } from "../../Loader";

import {
  DayDelimiterLine,
  DayDelimiterRoot,
  DayDelimiterText,
  PreviousMessagesLoaderContainer,
  Root
} from "./styles";
import { parseServerDateTime } from "../../../utils/time";
import styled from "styled-components/macro";
import { arraysAreEqual } from "../../../utils/helpers";

interface IChatMessagesList {
  chat: IFulfilledChat;
  messagesList?: { messages: TMessage[]; isFirstPage: boolean };
  onMessageRendered?: (message: TMessage) => void;
  onEditMessage: (message: TMessage) => void;
  onDeleteMessage: (message: TMessage) => void;
  isLoading?: boolean;
  isLoadingPreviousPage?: boolean;
  onScrollTop: () => void;
}

const DayDelimiter = React.memo<{ children: React.ReactNode }>(
  ({ children }) => {
    return (
      <DayDelimiterRoot>
        <DayDelimiterLine />
        <DayDelimiterText>{children}</DayDelimiterText>
        <DayDelimiterLine />
      </DayDelimiterRoot>
    );
  }
);

export const ChatMessagesList: React.FC<IChatMessagesList> = ({
  chat,
  messagesList,
  onMessageRendered,
  onEditMessage,
  onDeleteMessage,
  isLoading,
  isLoadingPreviousPage,
  onScrollTop
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const lastMessageAnchorRef = useRef<HTMLDivElement>(null);

  const messages = messagesList?.messages ?? [];
  const messagesRef = useRef<TMessage[]>([]);
  const isLoadingRef = useRef<boolean | undefined>(isLoading);

  isLoadingRef.current = isLoading;
  messagesRef.current = messages;

  useEffect(() => {
    if (!rootRef?.current) {
      return;
    }
    const removeListener = addScrollOnTopListener(
      rootRef.current,
      () => {
        if (messagesRef.current.length > 0 && !isLoadingRef.current) {
          onScrollTop();
        }
      },
      0
    );

    return () => {
      removeListener();
    };
  }, [onScrollTop]);

  return (
    <Root ref={rootRef}>
      {isLoadingPreviousPage ? (
        <PreviousMessagesLoaderContainer>
          <ThreeDotsLoader />
        </PreviousMessagesLoaderContainer>
      ) : null}
      {isLoading ? (
        <Space style={{ width: "100%" }} direction={"vertical"} size={"large"}>
          <Skeleton avatar active />
          <Skeleton avatar active />
        </Space>
      ) : (
        <MessagesList
          key={chat.chat_id} // use key to create new react element for each chat
          messages={messages}
          chat={chat}
          onEditMessage={onEditMessage}
          onDeleteMessage={onDeleteMessage}
          rootRef={rootRef}
          lastMessageAnchorRef={lastMessageAnchorRef} // TODO: remove after testing on all browsers
          onMessageRendered={onMessageRendered}
        />
      )}
      <div ref={lastMessageAnchorRef}></div>
    </Root>
  );
};

const MessagesListRoot = styled.div``;

const callScroll = (fn: () => void, delay = 50) => {
  setTimeout(fn, delay);
};

const MessagesList: React.FC<{
  messages: TMessage[];
  chat: IFulfilledChat;
  onMessageRendered?: (message: TMessage) => void;
  onEditMessage: (message: TMessage) => void;
  onDeleteMessage: (message: TMessage) => void;
  rootRef: React.MutableRefObject<HTMLDivElement | null>;
  lastMessageAnchorRef: React.MutableRefObject<HTMLDivElement | null>;
}> = ({
  chat,
  messages,
  onMessageRendered,
  onDeleteMessage,
  onEditMessage,
  rootRef,
  lastMessageAnchorRef
}) => {
  const today = new Date();
  const previousMessages = useRef<TMessage[]>([]);
  const previousContentHeight = useRef<number>(0);

  useEffect(() => {
    if (!rootRef?.current || !lastMessageAnchorRef?.current) {
      return;
    }

    const currentContentHeight = rootRef.current.scrollHeight;

    if (
      arraysAreEqual(
        previousMessages.current.map((c) => c.chat_id),
        messages.map((c) => c.chat_id)
      )
    ) {
      previousContentHeight.current = currentContentHeight;
      return;
    }
    // Messages changed 0 -> N
    else if (previousMessages.current.length === 0 && messages.length > 0) {
      callScroll(() => {
        if (!rootRef.current) {
          return;
        }
        scrollElementTo(
          rootRef.current,
          currentContentHeight - rootRef.current.clientHeight
        );
      });
    }
    // Messages changed N -> N + 1, and last one is different
    else if (
      previousMessages.current.length === messages.length - 1 &&
      previousMessages.current[previousMessages.current.length - 1]
        .created_at !== messages[messages.length - 1].created_at
    ) {
      callScroll(() => {
        if (!rootRef.current) {
          return;
        }
        scrollElementTo(rootRef.current, currentContentHeight);
      });
    }
    // Messages changed N -> M + N, messages added on top
    else if (previousMessages.current.length !== messages.length) {
      let lastEqualIndex = -1;
      for (let idx = 0; idx < messages.length; idx++) {
        if (
          previousMessages.current[previousMessages.current.length - 1 - idx] &&
          messages[messages.length - 1 - idx].created_at ===
            previousMessages.current[previousMessages.current.length - 1 - idx]
              .created_at
        ) {
          lastEqualIndex = idx;
        } else {
          break;
        }
      }

      if (lastEqualIndex !== -1) {
        const scrollTo = currentContentHeight - previousContentHeight.current;
        callScroll(() => {
          if (!rootRef.current) {
            return;
          }
          scrollElementTo(rootRef.current, scrollTo);
        }, 0);
      }
    }

    previousContentHeight.current = currentContentHeight;
    previousMessages.current = messages;
  }, [messages]);

  return (
    <MessagesListRoot>
      {messages.map((m, index) => {
        const isPreviousMessageSameDay =
          messages[index - 1] &&
          format(parseServerDateTime(m.created_at), "yyyy/LL/dd") ===
            format(
              parseServerDateTime(messages[index - 1].created_at),
              "yyyy/LL/dd"
            );

        const isToday =
          format(parseServerDateTime(m.created_at), "yyyy/LL/dd") ===
          format(today, "yyyy/LL/dd");

        return (
          <React.Fragment key={m.created_at}>
            {!isPreviousMessageSameDay ? (
              <DayDelimiter>
                {isToday
                  ? "Today"
                  : format(parseServerDateTime(m.created_at), "dd MMM yyyy")}
              </DayDelimiter>
            ) : null}
            <MessageItem
              chat={chat}
              message={m}
              previousMessage={index > 0 ? messages[index - 1] : undefined}
              onRendered={onMessageRendered}
              onEdit={onEditMessage}
              onDelete={onDeleteMessage}
              isCreating={m.operation === MessageOperation.CREATING}
              isDeleting={m.operation === MessageOperation.DELETING}
              isUpdating={m.operation === MessageOperation.UPDATING}
            />
          </React.Fragment>
        );
      })}
    </MessagesListRoot>
  );
};
